import React, { useRef, useEffect } from "react";
import { Pie } from "react-chartjs-2";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { getCategoryLabelById } from "../../../utils/labelsById";
import useExportChartImage from "../useExportChartImage";
import SaveButton from "../UI/SaveButton";

ChartJS.register(ArcElement, Tooltip, Legend, ChartDataLabels);

const COLOR_PALETTE = [
  "#6baed6",
  "#e07b39",
  "#bdbdbd",
  "#f2c94c",
  "#4271c3",
  "#9b59b6",
  "#84bd00",
  "#ff7f0e",
  "#2ca02c",
  "#d62728",
];

const generateColorMap = (labels) => {
  const colorMap = {};
  labels.forEach((label, index) => {
    colorMap[label] = COLOR_PALETTE[index % COLOR_PALETTE.length];
  });
  return colorMap;
};

const extractNumericValue = (label) => {
  const match = label.match(/\d+/g);
  return match ? parseInt(match[0]) : Number.MAX_SAFE_INTEGER;
};

const MultiPieChart = ({ data = {}, dataSetting, title }) => {
  const chartRefs = useRef([]);
  const exportChartImage = useExportChartImage();

  const groupKeys = Object.keys(data);
  const categorySet = new Set();
  groupKeys.forEach((group) => {
    Object.keys(data[group]).forEach((key) => categorySet.add(key));
  });
  const categories = Array.from(categorySet).sort((a, b) => {
    return extractNumericValue(a) - extractNumericValue(b);
  });

  const colorMap = generateColorMap(categories);

  const options = {
    layout: {
      padding: 40,
    },
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (context) => `${context.label}: ${context.raw}%`,
        },
      },
      datalabels: {
        color: "#000",
        font: {
          weight: "bold",
          size: 14,
        },
        formatter: (value) => `${value}%`,
        anchor: "end",
        align: "end",
        offset: 4,
      },
    },
  };

  // Ruaj gjatësinë pa fshirë referencat ekzistuese
  useEffect(() => {
    if (chartRefs.current.length < groupKeys.length) {
      chartRefs.current.length = groupKeys.length;
    }
  }, [groupKeys.length]);

  return (
    <div>
      {title && (
        <h3 style={{ marginBottom: "20px" }}>{getCategoryLabelById(title)}</h3>
      )}
      <div style={{ display: "flex", flexDirection: "column", gap: "60px" }}>
        {groupKeys.map((groupKey, idx) => {
          const groupData = data[groupKey];
          const total = Object.values(groupData).reduce((a, b) => a + b, 0);
          const datasetValues = categories.map((cat) => {
            const val = groupData[cat] || 0;
            return total > 0 ? parseFloat(((val / total) * 100).toFixed(2)) : 0;
          });

          const chartData = {
            labels: categories,
            datasets: [
              {
                data: datasetValues,
                backgroundColor: categories.map(
                  (cat) => colorMap[cat] || "#8884d8"
                ),
                borderColor: "#fff",
                borderWidth: 2,
              },
            ],
          };

          const handleExportImage = () => {
            const fileName = `${dataSetting?.name}_${dataSetting?.year}_${groupKey}`;
            exportChartImage({
              chartRef: { current: chartRefs.current[idx] },
              fileName,
              dataSetting: {
                ...dataSetting,
                name: fileName,
              },
            });
          };

          return (
            <div key={groupKey}>
              <div
                ref={(el) => (chartRefs.current[idx] = el)}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                }}
              >
                <h4 style={{ textTransform: "capitalize" }}>{groupKey}</h4>
                <div style={{ width: "300px" }}>
                  <Pie data={chartData} options={options} />
                </div>
                <div style={{ marginTop: "10px" }}>
                  {categories.map((cat, i) => (
                    <div
                      key={i}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                        marginBottom: "4px",
                      }}
                    >
                      <div
                        style={{
                          width: "12px",
                          height: "12px",
                          backgroundColor: colorMap[cat] || "#8884d8",
                          borderRadius: "2px",
                        }}
                      />
                      <span style={{ fontSize: "14px", color: "#333" }}>
                        {cat}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
              <SaveButton onClick={handleExportImage} />
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default MultiPieChart;
